import * as React from 'react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Attachment, deleteAttachment, SmRefType } from 'api/attachment';
import { getApiUrl } from 'api/useApi';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { AddIcon, DeleteIcon, EditIcon } from '@fluentui/react-icons';
import { formStyles } from 'styles/formStyles';
import { EditAttachment } from './EditAttachment';

const styles = mergeStyleSets({
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
    },
    title: {
        fontWeight: 600,
        marginTop: 5,
    },
    list: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr max-content max-content',
        gridColumnGap: 10,
    },
    addAction: {
        cursor: 'pointer',
        marginTop: 7,
    },
});

interface LinkedAttachmentListProps {
    kNr: number;
    smRefType: SmRefType;
    smRef: string;
    attachmentList: Attachment[];
    refetchKunde: () => void;
    onAdd: () => void;
    disabled?: boolean;
}

export const LinkedAttachmentList: React.FC<LinkedAttachmentListProps> = ({
    kNr,
    smRefType,
    smRef,
    attachmentList,
    refetchKunde,
    onAdd,
    disabled,
}) => {
    const auth = useSelector(authSelector);

    const [editAttachmentData, setEditAttachmentData] = React.useState<Attachment | null>(null);
    const [deleteAttachmentData, setDeleteAttachmentData] = React.useState<Attachment | null>(null);

    return (
        <>
            <div className={styles.header}>
                <div className={styles.title}>Dokumente</div>
                {disabled || <AddIcon className={styles.addAction} onClick={onAdd} />}
            </div>
            <div className={styles.list}>
                {attachmentList
                    .filter((attachment) => attachment.smRefType === smRefType && attachment.smRef === smRef)
                    .map((attachment) => {
                        const fileUrl =
                            getApiUrl('attachment') +
                            '/akte?kNr=' +
                            kNr +
                            '&fileName=' +
                            encodeURIComponent(attachment.file);
                        return (
                            <React.Fragment key={fileUrl}>
                                <div>{attachment.text}</div>
                                <div>
                                    <a href={fileUrl} rel="noreferrer" target="_blank">
                                        {attachment.file}
                                    </a>
                                    {attachment.anzeige &&
                                    (auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX')
                                        ? ' (für AD sichtbar)'
                                        : ''}
                                </div>
                                {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                                    <>
                                        <EditIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setEditAttachmentData(attachment)}
                                        />
                                        <DeleteIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setDeleteAttachmentData(attachment)}
                                        />
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
            </div>
            {editAttachmentData && (
                <EditAttachment
                    attachment={editAttachmentData}
                    onClose={(refetch) => {
                        if (refetch) {
                            refetchKunde();
                        }
                        setEditAttachmentData(null);
                    }}
                />
            )}
            {deleteAttachmentData && (
                <ConfirmMessage
                    title="Dokument löschen"
                    minWidth={500}
                    content={
                        <>
                            Soll das Dokument {deleteAttachmentData.file + ' / ' + deleteAttachmentData.text} gelöscht
                            werden?
                            <br />
                            <strong>Wichtig: das Dokument ist dann auch beim Kunden gelöscht!</strong>
                        </>
                    }
                    onConfirm={() => {
                        if (deleteAttachmentData) {
                            deleteAttachment(deleteAttachmentData).then(() => {
                                refetchKunde();
                                setDeleteAttachmentData(null);
                            });
                        }
                    }}
                    onCancel={() => setDeleteAttachmentData(null)}
                />
            )}
        </>
    );
};
